
"use client"

import { useState, useEffect, useRef } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Label } from "../ui/label"
import { X, Save } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"

interface EditApproverModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: any) => void
  employee: {
    userId: number
    userName: string
    primaryApproverName?: string
    secondaryApproverName?: string
  }
  onFetchManagers: () => Promise<{ id: number; name: string }[]>
  onFetchSecondaryUsers: () => Promise<{ id: number; name: string }[]>
}

export function EditApproverModal({
  isOpen,
  onClose,
  onSave,
  employee,
  onFetchManagers,
  onFetchSecondaryUsers
}: EditApproverModalProps) {
  const [formData, setFormData] = useState({
    primaryApprover: "",
    secondaryApprover: ""
  })

  const [openPrimary, setOpenPrimary] = useState(false)
  const [openSecondary, setOpenSecondary] = useState(false)

  const [managers, setManagers] = useState<{ id: number; name: string }[]>([])
  const [secondaryUsers, setSecondaryUsers] = useState<{ id: number; name: string }[]>([])

  const [loadingManagers, setLoadingManagers] = useState(false)
  const [loadingSecondaryUsers, setLoadingSecondaryUsers] = useState(false)

  const [primarySearch, setPrimarySearch] = useState("")
  const [secondarySearch, setSecondarySearch] = useState("")
  const [validationErrors, setValidationErrors] = useState<{
    primaryApprover?: string;
  }>({})

  const isClosingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setPrimarySearch("");
      setSecondarySearch("");
      setValidationErrors({});
      // Close all dropdowns when modal closes
      setOpenPrimary(false);
      setOpenSecondary(false);
      // Reset data when modal closes
      setManagers([]);
      setSecondaryUsers([]);
      isClosingRef.current = false;
    }
  }, [isOpen]);

  // Fetch managers and secondary users when modal opens (Edit button click)
  useEffect(() => {
    if (!isOpen || !employee) return;

    const loadData = async () => {
      setLoadingManagers(true);
      setLoadingSecondaryUsers(true);
      try {
        const [fetchedManagers, fetchedUsers] = await Promise.all([
          onFetchManagers(),
          onFetchSecondaryUsers()
        ]);
        setManagers(fetchedManagers);
        setSecondaryUsers(fetchedUsers);

        const updates: { primaryApprover?: string; secondaryApprover?: string } = {};
        if (employee.primaryApproverName) {
          const primaryManager = fetchedManagers.find(m => m.name === employee.primaryApproverName);
          if (primaryManager) {
            updates.primaryApprover = primaryManager.id.toString();
          }
        }
        if (employee.secondaryApproverName && employee.secondaryApproverName !== "--") {
          const secondary = fetchedUsers.find(u => u.name === employee.secondaryApproverName);
          if (secondary) {
            updates.secondaryApprover = secondary.id.toString();
          } else {
            updates.secondaryApprover = "none";
          }
        } else {
          updates.secondaryApprover = "none";
        }
        setFormData(prev => ({ ...prev, ...updates }));
      } finally {
        setLoadingManagers(false);
        setLoadingSecondaryUsers(false);
      }
    };
    loadData();
  }, [isOpen, employee?.userId, employee?.primaryApproverName, employee?.secondaryApproverName]);

  // Handle dropdown open/close (no API calls – data already loaded when modal opened)
  const handlePrimaryDropdownChange = (open: boolean) => {
    setOpenPrimary(open);
  };

  const handleSecondaryDropdownChange = (open: boolean) => {
    setOpenSecondary(open);
  };



  const filteredPrimaryManagers = managers.filter(m =>
    m.name.toLowerCase().includes(primarySearch.toLowerCase())
  )

  const filteredSecondaryUsers = secondaryUsers.filter(u =>
    u.name.toLowerCase().includes(secondarySearch.toLowerCase())
  )

  const handleClose = () => {
    // Prevent multiple close calls
    if (isClosingRef.current) {
      return;
    }

    // Close all dropdowns first to prevent ResizeObserver errors
    if (openPrimary || openSecondary) {
      isClosingRef.current = true;
      setOpenPrimary(false);
      setOpenSecondary(false);
      // Use a small delay to allow dropdowns to close before modal closes
      setTimeout(() => {
        isClosingRef.current = false;
        onClose();
      }, 50);
    } else {
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isClosingRef.current) {
      handleClose();
    }
  };

  const handleSave = () => {
    const errors: { primaryApprover?: string } = {};
    
    if (!formData.primaryApprover || formData.primaryApprover === "") {
      errors.primaryApprover = "Primary Approver is required";
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});

    // Close all dropdowns before saving
    setOpenPrimary(false);
    setOpenSecondary(false);

    onSave({
      userID: employee.userId,
      primaryApprover: Number(formData.primaryApprover),
      secondaryApprover: formData.secondaryApprover === "none" || !formData.secondaryApprover ? 0 : Number(formData.secondaryApprover),
      modUserID: employee.userId
    })

    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border border-gray-200">
        <DialogHeader className="border-b border-gray-100 pb-4">
          <DialogTitle className="text-center text-xl font-semibold text-gray-900">
            Edit Approver
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">{employee.userName}</h3>
          </div>

          {/* Primary Approver */}
          <div className="space-y-2">
            <Label htmlFor="primary" className="text-sm font-medium text-gray-700">Primary Approver</Label>
            <Popover open={openPrimary} onOpenChange={handlePrimaryDropdownChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={`w-full justify-between text-left font-normal bg-white text-gray-900 hover:bg-white ${
                    validationErrors.primaryApprover ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <span className={formData.primaryApprover ? "" : "text-gray-500"}>
                    {formData.primaryApprover
                      ? managers.find(m => m.id.toString() === formData.primaryApprover)?.name
                      : "Select Primary Approver"}
                  </span>
                  <span className="text-gray-400">▼</span>
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border border-gray-200">
                {loadingManagers ? (
                  <div className="py-6 text-center text-sm text-gray-500">Loading...</div>
                ) : (
                  <>
                    <div className="border-b border-gray-200 p-2">
                  <input
                    type="text"
                    placeholder="Search approver..."
                    value={primarySearch}
                    onChange={(e) => setPrimarySearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div 
                  className="max-h-48 overflow-y-auto overscroll-contain"
                  onWheel={(e) => {
                    const target = e.currentTarget;
                    const isScrollable = target.scrollHeight > target.clientHeight;
                    const isAtTop = target.scrollTop === 0;
                    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight;
                    
                    if (isScrollable) {
                      if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
                        // At boundary, allow event to propagate
                        return;
                      }
                      // Stop propagation to prevent Popover from handling it
                      e.stopPropagation();
                    }
                  }}
                  style={{ 
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth'
                  }}
                >
                  {filteredPrimaryManagers.length === 0 ? (
                    <div className="py-6 text-center text-sm text-gray-500">No approver found.</div>
                  ) : (
                    filteredPrimaryManagers.map(m => (
                      <div
                        key={m.id}
                        onClick={() => {
                          setFormData({ ...formData, primaryApprover: m.id.toString() })
                          setOpenPrimary(false)
                          setPrimarySearch("")
                          if (validationErrors.primaryApprover) {
                            setValidationErrors({ ...validationErrors, primaryApprover: undefined })
                          }
                        }}
                        className="px-3 py-2 text-sm cursor-pointer text-gray-900 hover:bg-gray-50"
                      >
                        {m.name}
                      </div>
                    ))
                  )}
                </div>
                  </>
                )}
              </PopoverContent>
            </Popover>
            {validationErrors.primaryApprover && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.primaryApprover}</p>
            )}
          </div>

          {/* Secondary Approver */}
          <div className="space-y-2">
            <Label htmlFor="secondary" className="text-sm font-medium text-gray-700">Secondary Approver</Label>
            <Popover open={openSecondary} onOpenChange={handleSecondaryDropdownChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between text-left font-normal bg-white border-gray-200  text-gray-900 hover:bg-white"
                >
                  <span className={formData.secondaryApprover ? "" : "text-gray-500"}>
                    {formData.secondaryApprover === "none"
                      ? "None"
                      : formData.secondaryApprover
                        ? secondaryUsers.find(u => u.id.toString() === formData.secondaryApprover)?.name
                        : "Select Secondary Approver"}
                  </span>
                  <span className="text-gray-400">▼</span>
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border border-gray-200">
                {loadingSecondaryUsers ? (
                  <div className="py-6 text-center text-sm text-gray-500">Loading...</div>
                ) : (
                  <>
                    <div className="border-b border-gray-200 p-2">
                  <input
                    type="text"
                    placeholder="Search secondary approver..."
                    value={secondarySearch}
                    onChange={(e) => setSecondarySearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div 
                  className="max-h-48 overflow-y-auto overscroll-contain"
                  onWheel={(e) => {
                    const target = e.currentTarget;
                    const isScrollable = target.scrollHeight > target.clientHeight;
                    const isAtTop = target.scrollTop === 0;
                    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight;
                    
                    if (isScrollable) {
                      if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
                        // At boundary, allow event to propagate
                        return;
                      }
                      // Stop propagation to prevent Popover from handling it
                      e.stopPropagation();
                    }
                  }}
                  style={{ 
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth'
                  }}
                >
                  <div
                    onClick={() => {
                      setFormData({ ...formData, secondaryApprover: "none" })
                      setOpenSecondary(false)
                      setSecondarySearch("")
                    }}
                    className="px-3 py-2 text-sm cursor-pointer text-gray-900 hover:bg-gray-50"
                  >
                    None
                  </div>
                  {filteredSecondaryUsers.length === 0 && secondarySearch ? (
                    <div className="py-6 text-center text-sm text-gray-500">No approver found.</div>
                  ) : (
                    filteredSecondaryUsers.map(u => (
                      <div
                        key={u.id}
                        onClick={() => {
                          setFormData({ ...formData, secondaryApprover: u.id.toString() })
                          setOpenSecondary(false)
                          setSecondarySearch("")
                        }}
                        className="px-3 py-2 text-sm cursor-pointer text-gray-900 hover:bg-gray-50"
                      >
                        {u.name}
                      </div>
                    ))
                  )}
                </div>
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="destructive" onClick={handleClose} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white">
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button onClick={handleSave} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            <Save className="h-4 w-4" /> Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
